import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import {
  Save as SaveIcon,
  Close as CloseIcon
} from '@mui/icons-material';

// Services
import invoiceService from '../services/invoiceService';

// Schemas
import { invoicePaymentSchema } from '../schemas/invoiceSchemas';

const PaymentUpdateModal = ({ open, onClose, invoice, onSuccess }) => {
  // State
  const [formData, setFormData] = useState({
    paid_amount: 0,
    payment_method: 'card',
    notes: ''
  });
  
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Load data
  useEffect(() => {
    if (open && invoice) {
      setFormData({
        paid_amount: invoice.paid_amount || 0,
        payment_method: invoice.payment_method || 'card',
        notes: ''
      });
      setErrors({});
    }
  }, [open, invoice]);

  // Handlers
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleSubmit = async () => {
    try {
      // Validate form
      await invoicePaymentSchema.validate(formData, { abortEarly: false });
      setErrors({});

      // Submit
      setSubmitting(true);
      await invoiceService.updateInvoicePayment(invoice.id, formData);

      onSuccess();
    } catch (err) {
      if (err.inner) {
        const validationErrors = {};
        err.inner.forEach(error => {
          validationErrors[error.path] = error.message;
        });
        setErrors(validationErrors);
      } else {
        console.error('Error updating payment:', err);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const calculateBalance = () => {
    if (!invoice) return 0;
    return invoice.total_amount - (invoice.paid_amount || 0);
  };

  const balance = calculateBalance();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">Ενημέρωση Πληρωμής</Typography>
          <Button
            startIcon={<CloseIcon />}
            onClick={onClose}
            color="inherit"
          >
            Κλείσιμο
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent>
        {invoice && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Στοιχεία Τιμολογίου
            </Typography>
            <Card variant="outlined">
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Αριθμός:
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {invoice.invoice_number}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Σύνολο:
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {invoiceService.formatCurrency(invoice.total_amount)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Εξοφλημένο:
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {invoiceService.formatCurrency(invoice.paid_amount || 0)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Υπόλοιπο:
                    </Typography>
                    <Typography 
                      variant="body1" 
                      fontWeight="medium"
                      color={balance > 0 ? 'error.main' : 'success.main'}
                    >
                      {invoiceService.formatCurrency(balance)}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Box>
        )}

        <Divider sx={{ mb: 3 }} />

        <Typography variant="h6" gutterBottom>
          Νέα Πληρωμή
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Ποσό Πληρωμής *"
              type="number"
              value={formData.paid_amount}
              onChange={(e) => handleInputChange('paid_amount', parseFloat(e.target.value) || 0)}
              error={!!errors.paid_amount}
              helperText={errors.paid_amount}
              inputProps={{ min: 0, step: 0.01 }}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Μέθοδος Πληρωμής</InputLabel>
              <Select
                value={formData.payment_method}
                onChange={(e) => handleInputChange('payment_method', e.target.value)}
                label="Μέθοδος Πληρωμής"
              >
                <MenuItem value="cash">Μετρητά</MenuItem>
                <MenuItem value="bank_transfer">Τραπεζική Μεταφορά</MenuItem>
                <MenuItem value="card">Κάρτα</MenuItem>
                <MenuItem value="check">Επιταγή</MenuItem>
                <MenuItem value="other">Άλλο</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Σημειώσεις"
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              error={!!errors.notes}
              helperText={errors.notes}
            />
          </Grid>
        </Grid>

        {balance > 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Το υπόλοιπο του τιμολογίου είναι {invoiceService.formatCurrency(balance)}.
            Εάν πληρώσετε το πλήρες ποσό, το τιμολόγιο θα σημειωθεί ως πληρωμένο.
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} disabled={submitting}>
          Ακύρωση
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={20} /> : <SaveIcon />}
        >
          {submitting ? 'Αποθήκευση...' : 'Ενημέρωση Πληρωμής'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentUpdateModal;
